import { hasPendingKeysSecretsRotationAtom, themeAtom } from "../../../shared/atoms";
import { log, logE } from "../../../shared/services/debug-logger";
import {
  AdminKeysSecretsPageResponse,
  AdminKeysSecretsRotationStatusResponse,
} from "../../models/admin-keys-secrets.models";
import {
  getAdminKeysSecrets,
  getAdminKeysSecretsErrorMessage,
  markAdminKeysSecretsRotationDone,
  saveAdminKeysSecretsMailbox,
} from "../../services/admin-keys-secrets.service";
import AdminKeysSecretsEnvironmentTable from "./AdminKeysSecretsEnvironmentTable";
import AdminKeysSecretsMailboxModal from "./AdminKeysSecretsMailboxModal";
import AdminKeysSecretsRotationBar from "./AdminKeysSecretsRotationBar";
import AdminKeysSecretsStatus from "./AdminKeysSecretsStatus";
import AdminKeysSecretsTopBar from "./AdminKeysSecretsTopBar";
import { useAtomValue, useSetAtom } from "jotai";
import { useCallback, useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";

type LoadingState = "loading" | "ready" | "error";

const emptyKeysSecretsPage: AdminKeysSecretsPageResponse = {
  mailbox: "",
  mailboxConfigured: false,
  environments: [],
  hasPendingRotation: false,
  isRotationOverdue: false,
  canMarkRotationDone: false,
  isWaitingForRotationBatch: false,
};

/**
 * Coordinates the Admin-only key rotation view while the credential cells render redacted values.
 */
const AdminKeysSecretsPanel = () => {
  const theme = useAtomValue(themeAtom);
  const setHasPendingKeysSecretsRotation = useSetAtom(hasPendingKeysSecretsRotationAtom);
  const [keysSecretsPage, setKeysSecretsPage] = useState<AdminKeysSecretsPageResponse>(emptyKeysSecretsPage);
  const [loadingState, setLoadingState] = useState<LoadingState>("loading");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isMailboxModalOpen, setIsMailboxModalOpen] = useState(false);
  const [mailboxModalErrorMessage, setMailboxModalErrorMessage] = useState<string | null>(null);
  const [isMailboxSaving, setIsMailboxSaving] = useState(false);
  const [isRotationSaving, setIsRotationSaving] = useState(false);

  const applyRotationStatus = useCallback(
    (rotationStatus: AdminKeysSecretsRotationStatusResponse) => {
      setKeysSecretsPage((currentPage) => ({
        ...currentPage,
        ...rotationStatus,
      }));
      setHasPendingKeysSecretsRotation(rotationStatus.hasPendingRotation);
    },
    [setHasPendingKeysSecretsRotation],
  );

  const loadKeysSecretsPage = useCallback(async () => {
    setLoadingState("loading");
    setErrorMessage(null);

    try {
      const response = await getAdminKeysSecrets();
      setKeysSecretsPage(response);
      setHasPendingKeysSecretsRotation(response.hasPendingRotation);
      setLoadingState("ready");
    } catch (error) {
      setErrorMessage(getAdminKeysSecretsErrorMessage(error));
      setLoadingState("error");
    }
  }, [setHasPendingKeysSecretsRotation]);

  useEffect(() => {
    void loadKeysSecretsPage();
  }, [loadKeysSecretsPage]);

  useEffect(() => {
    if (!keysSecretsPage.nextMailboxScanAt) {
      return;
    }

    const nextMailboxScanTimestamp = Date.parse(keysSecretsPage.nextMailboxScanAt);
    if (!Number.isFinite(nextMailboxScanTimestamp)) {
      return;
    }

    const refreshDelay = Math.max(nextMailboxScanTimestamp - Date.now(), 1000);
    const refreshTimeoutId = window.setTimeout(() => {
      void loadKeysSecretsPage();
    }, refreshDelay);

    return () => window.clearTimeout(refreshTimeoutId);
  }, [keysSecretsPage.nextMailboxScanAt, loadKeysSecretsPage]);

  const handleMailboxSave = useCallback(
    async (mailbox: string) => {
      log("AdminKeysSecretsPanel - handleMailboxSave");
      setIsMailboxSaving(true);
      setMailboxModalErrorMessage(null);

      try {
        const rotationStatus = await saveAdminKeysSecretsMailbox({ mailbox });
        applyRotationStatus(rotationStatus);
        log("AdminKeysSecretsPanel - handleMailboxSave - applied", { configured: rotationStatus.mailboxConfigured });
        setIsMailboxModalOpen(false);
        await loadKeysSecretsPage();
      } catch (error) {
        logE(error, { operation: "AdminKeysSecretsPanel - handleMailboxSave - failed" });
        setMailboxModalErrorMessage(getAdminKeysSecretsErrorMessage(error));
      } finally {
        setIsMailboxSaving(false);
      }
    },
    [applyRotationStatus, loadKeysSecretsPage],
  );

  const handleRotationDone = useCallback(async () => {
    log("AdminKeysSecretsPanel - handleRotationDone");
    setIsRotationSaving(true);
    setErrorMessage(null);
    setLoadingState("ready");

    try {
      const rotationStatus = await markAdminKeysSecretsRotationDone();
      applyRotationStatus(rotationStatus);
      log("AdminKeysSecretsPanel - handleRotationDone - applied", { pending: rotationStatus.hasPendingRotation });
      setLoadingState("ready");
    } catch (error) {
      logE(error, { operation: "AdminKeysSecretsPanel - handleRotationDone - failed" });
      setErrorMessage(getAdminKeysSecretsErrorMessage(error));
      setLoadingState("error");
    } finally {
      setIsRotationSaving(false);
    }
  }, [applyRotationStatus]);

  return (
    <main
      data-test="admin-keys-secrets-page"
      className={twMerge("flex min-h-0 min-w-0 flex-1 overflow-hidden", theme.home.tailwind.textDefault)}
    >
      <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col" data-test="admin-keys-secrets-admin">
        <AdminKeysSecretsTopBar
          mailbox={keysSecretsPage.mailbox}
          isLoading={loadingState === "loading"}
          onChangeMailbox={() => {
            setMailboxModalErrorMessage(null);
            setIsMailboxModalOpen(true);
          }}
        />
        <AdminKeysSecretsStatus
          loadingState={loadingState}
          errorMessage={errorMessage}
          mailboxScanError={keysSecretsPage.mailboxScanError}
          isWaitingForRotationBatch={keysSecretsPage.isWaitingForRotationBatch}
          nextMailboxScanAt={keysSecretsPage.nextMailboxScanAt}
        />
        <div className="min-h-0 flex-1 overflow-y-auto bg-[#F8FBFA] p-4 dark:bg-gray-900">
          <div className="flex max-w-full flex-col gap-4">
            <AdminKeysSecretsRotationBar
              mailboxConfigured={keysSecretsPage.mailboxConfigured}
              lastRotation={keysSecretsPage.lastRotation}
              hasPendingRotation={keysSecretsPage.hasPendingRotation}
              isRotationOverdue={keysSecretsPage.isRotationOverdue}
              rotationDueDate={keysSecretsPage.rotationDueDate}
              canMarkRotationDone={keysSecretsPage.canMarkRotationDone}
              isSaving={isRotationSaving}
              onRotationDone={handleRotationDone}
            />
            <AdminKeysSecretsEnvironmentTable environments={keysSecretsPage.environments} />
          </div>
        </div>
      </div>
      {isMailboxModalOpen && (
        <AdminKeysSecretsMailboxModal
          mailbox={keysSecretsPage.mailbox}
          saving={isMailboxSaving}
          errorMessage={mailboxModalErrorMessage}
          onClose={() => {
            if (!isMailboxSaving) {
              setIsMailboxModalOpen(false);
              setMailboxModalErrorMessage(null);
            }
          }}
          onSave={handleMailboxSave}
        />
      )}
    </main>
  );
};

export default AdminKeysSecretsPanel;
